import { environment } from 'src/environments/environment'
import { ChatOutbox, ChatSendErrorCode, OutboxEntry } from '../ngrx-store/app.state'

/**
 * What the sender is told about a message that wasn't sent, and whether it's resent by itself when
 * the socket (re)connects. The codes are the backend's CHAT_SEND_ERRORS (sockets/routes.js in
 * corpers.online) plus TIMEOUT - keep the two lists in step. An unknown code is treated like
 * SERVER_ERROR.
 */
const CHAT_SEND_ERRORS: Record<ChatSendErrorCode, { copy: string; autoRetry: boolean }> = {
  INVALID_MESSAGE: { copy: "Message not sent - it couldn't be read. Try editing it.", autoRetry: false },
  RECIPIENT_NOT_FOUND: { copy: "Message not sent - we couldn't find who it's for.", autoRetry: false },
  NOT_A_PARTICIPANT: { copy: "Message not sent - you can't send messages to this chat.", autoRetry: false },
  // The socket's identity is resolved again on the next connection.
  NOT_IDENTIFIED: { copy: "Message not sent - we couldn't confirm it's you. Tap it to try again.", autoRetry: true },
  SERVER_ERROR: { copy: 'Message not sent. Tap it to try again.', autoRetry: true },
  TIMEOUT: { copy: 'Message not sent - the connection is slow. Tap it to try again.', autoRetry: true },
}

export function chatSendErrorCode(code: unknown): ChatSendErrorCode {
  return typeof code === 'string' && code in CHAT_SEND_ERRORS ? (code as ChatSendErrorCode) : 'SERVER_ERROR'
}

export function chatSendErrorCopy(code: ChatSendErrorCode): string {
  return CHAT_SEND_ERRORS[code].copy
}

export function chatSendErrorAutoRetries(code: ChatSendErrorCode): boolean {
  return CHAT_SEND_ERRORS[code].autoRetry
}

/**
 * A new message's client_msg_id (the backend accepts 8-64 of A-Z, a-z, 0-9, - and _). Not
 * crypto.randomUUID(): that's only there on https and localhost, and `yarn local-serve` is
 * plain http on the network IP. Only has to be unique per sender.
 */
export function newClientMsgId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`
}

/** A restored message older than this isn't resent by itself - it shows as "Not sent" instead. */
export const OUTBOX_AUTO_RESEND_MAX_AGE_MS = 24 * 60 * 60 * 1000
/** At most this many are kept on the device (the newest). */
export const OUTBOX_MAX_STORED = 50

const OUTBOX_STORAGE_KEY = '_chat_outbox_' + environment.env

type StoredOutbox = { owner: string; entries: OutboxEntry[] }

/**
 * Keeps the outbox on this device, for `owner` (their chat identity), so unsent messages survive
 * a reload. One key for everyone: the next person to log in never sees - or sends - another's.
 */
export function saveOutbox(owner: string | undefined, outbox: ChatOutbox): void {
  try {
    const entries = Object.values(outbox)
      .sort((a, b) => a.message.created_at.localeCompare(b.message.created_at))
      .slice(-OUTBOX_MAX_STORED)
    if (!owner || !entries.length) {
      localStorage.removeItem(OUTBOX_STORAGE_KEY)
      return
    }
    localStorage.setItem(OUTBOX_STORAGE_KEY, JSON.stringify({ owner, entries } as StoredOutbox))
  } catch (error) {
    // Full or blocked storage: the outbox still works, it just won't survive a reload.
    console.log('could not save the chat outbox', error)
  }
}

/**
 * `owner`'s saved outbox, ready to go back in the store: none of it is still sending (that send
 * died with the page), so it waits to be resent - or, when older than a day, shows as "Not sent"
 * and waits for a tap (sending a day-old message by itself would surprise both people).
 * Anyone else's saved outbox is removed.
 */
export function loadOutbox(owner: string | undefined, now = Date.now()): OutboxEntry[] {
  try {
    const stored: StoredOutbox | null = JSON.parse(localStorage.getItem(OUTBOX_STORAGE_KEY) ?? 'null')
    if (!stored || !owner || stored.owner !== owner || !Array.isArray(stored.entries)) {
      if (stored) localStorage.removeItem(OUTBOX_STORAGE_KEY)
      return []
    }
    return stored.entries.map((entry): OutboxEntry => {
      const tooOld = now - new Date(entry.message.created_at).getTime() > OUTBOX_AUTO_RESEND_MAX_AGE_MS
      if (tooOld) return { ...entry, status: 'failed', error: entry.error ?? 'TIMEOUT', autoRetry: false }
      return entry.status === 'failed' ? entry : { ...entry, status: 'waiting' }
    })
  } catch (error) {
    console.log('could not load the chat outbox', error)
    return []
  }
}

export function clearStoredOutbox(): void {
  try {
    localStorage.removeItem(OUTBOX_STORAGE_KEY)
  } catch {
    // Nothing saved to clear.
  }
}
